import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type Favourites, type RankedTeam } from '../api'
import { Detail } from './Detail'
import { TeamBadge } from './TeamBadge'
import { Term } from './Term'

/**
 * Who the model thinks is strongest, and against whom.
 *
 * **Why a page of counterfactuals.** Every other view here is anchored to a match that exists. A
 * ranking is not: it asks what would happen if these sixteen teams played each other now, which is
 * the only way to compare sides that have not met and, in a bracket, mostly never will. Nothing on
 * this page is recorded, scored or settled — it is recomputed from current ratings on every request.
 *
 * **No table.** The obvious rendering of a round robin is a sixteen-by-sixteen matrix, and
 * `mobile.mjs` is right to fail one: a table whose columns do not fit is half a table, and hiding
 * half of it behind a horizontal scroll on a phone is not a design decision, it is a defect with a
 * scrollbar. The same numbers live one tap away instead, as a team's own row read top to bottom —
 * which is also the only view anyone wants, since nobody reads a matrix cell-wise.
 */
export function FavouritesPage({ leagueId }: { leagueId: number }) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['favourites', leagueId],
    queryFn: () => api.favourites(leagueId),
    // Five minutes, not sixty seconds. The inputs move when a match resolves, and a table that
    // reshuffles under a reader who is comparing two rows is worse than one a few minutes stale.
    refetchInterval: 300_000,
  })

  if (isPending) {
    return (
      <div className="space-y-6" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading…</span>
        <div className="h-8 w-64 max-w-full animate-pulse rounded bg-surface-raised motion-reduce:animate-none" />
        <div className="space-y-2">
          {Array.from({ length: 8 }, (_, index) => (
            <div
              key={index}
              className="h-14 animate-pulse rounded-lg bg-surface motion-reduce:animate-none"
            />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-danger-edge bg-danger-bg px-4 py-3 text-sm">
        <p className="font-medium text-danger">Could not load the strength ranking.</p>
        <p className="mt-1 text-ink-soft">
          The tournament&rsquo;s matches, the bet record and the model page load separately and may
          still be available.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 rounded border border-danger-edge px-2 py-1 text-xs text-danger transition-colors hover:bg-surface"
        >
          Try again
        </button>
      </div>
    )
  }

  if (data.teams.length < 2) {
    return (
      <div className="space-y-4">
        <Heading data={data} />
        <p className="rounded border border-line bg-surface px-4 py-8 text-center text-sm text-ink-soft">
          Not enough teams with a lineup we can resolve to rank anything yet.
        </p>
        <Unranked data={data} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Heading data={data} />
      <ol className="space-y-2">
        {data.teams.map((team) => (
          <TeamRow key={team.teamId} team={team} field={data.teams.length} />
        ))}
      </ol>
      <Limits data={data} />
      <Unranked data={data} />
    </div>
  )
}

function Heading({ data }: { data: Favourites }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold">Favourites</h2>
      <p className="mt-1 max-w-3xl text-sm text-ink-soft">
        Every team in {data.league.name ?? 'this tournament'} played against every other, right now,
        by the same model that prices the matches. The figure is the share of{' '}
        <Term id="map-market" define /> it wins across the whole field — a{' '}
        <Term id="pre-draft" define /> number, so it knows the rosters and not the heroes.
      </p>
    </div>
  )
}

/**
 * One team, with its row of pairings a tap away.
 *
 * The bar is the accent, never the outcome colours: green and red mean a bet won or lost everywhere
 * else on this site, and a table of teams shaded green-to-red would be reading a strength estimate
 * as a set of results.
 */
function TeamRow({ team, field }: { team: RankedTeam; field: number }) {
  const [open, setOpen] = useState(false)

  return (
    <li className="min-w-0 overflow-hidden rounded-lg border border-line bg-surface">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex min-h-11 w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-raised"
      >
        <span className="tabular w-5 shrink-0 text-sm text-ink-faint">{team.rank}</span>
        <TeamBadge name={team.name} logo={team.logo} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{team.name}</span>
          {team.rosterConfidence !== 'high' ? (
            <span className="block text-xs text-warn">
              {team.rosterConfidence === 'low' ? 'declared roster' : 'older lineup'}
            </span>
          ) : null}
        </span>
        <span className="w-20 shrink-0 sm:w-40">
          {/* The bar earns its width on a phone by being the only thing that lets two rows be
              compared without reading two numbers. It shrinks rather than wrapping the row. */}
          <span className="hidden h-2 overflow-hidden rounded-full bg-surface-raised sm:flex">
            <span className="bg-accent" style={{ width: `${team.vsField * 100}%` }} />
          </span>
          <span className="tabular block text-right text-sm sm:mt-1 sm:text-xs sm:text-ink-soft">
            {(team.vsField * 100).toFixed(1)}%
          </span>
        </span>
      </button>

      {open ? <Row team={team} field={field} /> : null}
    </li>
  )
}

/** A team's full row: who it beats, who beats it, and the five the number is about. */
function Row({ team, field }: { team: RankedTeam; field: number }) {
  return (
    <div className="border-t border-line px-3 py-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-ink-faint">
        <span>
          <Detail
            label="How this figure is worked out"
            heading={<span className="text-ink-soft">against the field</span>}
          >
            The mean of the {field - 1} pairings below. Each is scored twice — once with this team on
            radiant and once on dire — and averaged, because radiant wins slightly more often at
            equal strength and which side a team draws is decided in the lobby.
          </Detail>{' '}
          <span className="tabular text-ink">{(team.vsField * 100).toFixed(1)}%</span> a map,{' '}
          <span className="tabular text-ink">{(team.vsFieldBo3 * 100).toFixed(1)}%</span> a Bo3
        </span>
        <span>
          mean rating <span className="tabular text-ink-soft">{team.meanElo.toFixed(0)}</span>
        </span>
      </div>

      <ul className="mt-3 space-y-1">
        {team.opponents.map((opponent) => (
          <li key={opponent.teamId} className="flex items-center gap-2 text-xs">
            <span className="min-w-0 flex-1 truncate text-ink-soft">{opponent.name}</span>
            <span className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-surface-raised sm:flex">
              <span className="bg-accent" style={{ width: `${opponent.mapProbability * 100}%` }} />
            </span>
            <span className="tabular w-12 shrink-0 text-right">
              {(opponent.mapProbability * 100).toFixed(1)}%
            </span>
            <span className="tabular w-12 shrink-0 text-right text-ink-faint">
              {opponent.seriesProbability === null
                ? '—'
                : `${(opponent.seriesProbability * 100).toFixed(0)}%`}
            </span>
          </li>
        ))}
      </ul>

      <p className="mt-2 text-right text-xs text-ink-faint">map · Bo3</p>

      <div className="mt-3 border-t border-line pt-2">
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-faint">
          {team.lineup.map((player) => (
            <li key={player.accountId}>
              {player.name ?? player.accountId}{' '}
              <span className="tabular text-ink-soft">{player.elo.toFixed(0)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/**
 * What the ranking is not — once, at the foot of the page.
 *
 * Not repeated per row, and not placed above the table. A caveat printed sixteen times is furniture
 * the eye learns to skip, and one printed before the thing it qualifies is read by nobody who came
 * for the thing.
 */
function Limits({ data }: { data: Favourites }) {
  const spread = data.teams[0].vsField - data.teams[data.teams.length - 1].vsField

  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3 text-xs text-ink-soft">
      <h3 className="text-sm font-medium text-ink">What this ranking cannot tell you</h3>
      <p className="mt-2">
        The whole field spans {(spread * 100).toFixed(0)} points, and the gaps in the middle of it
        are smaller than what we can establish. Measured over 11.7M out-of-sample predictions, the
        model&rsquo;s entire feature stack buys about a third as much among elite teams as it does
        across tier-1 as a whole — this is the field where it knows least, and the ranking is
        correspondingly coarse. Treat the top and bottom as meaningful and a two-point gap as noise.
      </p>
      <p className="mt-2">
        It is also a <Term id="pre-draft" define /> estimate, and among teams this strong the draft
        is where most of what remains to be predicted actually lives. A ranking of teams before they
        draft is the weaker half of what the model knows.
      </p>
      <p className="mt-2">
        Nothing here is a tournament forecast. It is a round robin, and a bracket is not — seeding,
        the format and who a team happens to meet decide far more of an event than mean strength
        does.
      </p>
      <p className="mt-2 text-ink-faint">
        Model {data.modelVersion} · ratings current to{' '}
        {new Date(data.ratingsAsOf * 1000).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </p>
    </div>
  )
}

/** Teams in the tournament we could not rank. Named, because a field one team short looks whole. */
function Unranked({ data }: { data: Favourites }) {
  if (data.excluded.length === 0) {
    return null
  }

  return (
    <div className="rounded-lg border border-line px-4 py-3 text-xs text-ink-faint">
      <h3 className="font-medium text-ink-soft">Not ranked</h3>
      <ul className="mt-1 space-y-0.5">
        {data.excluded.map((team) => (
          <li key={team.teamId ?? team.name}>
            {team.name} — {team.reason}
          </li>
        ))}
      </ul>
    </div>
  )
}
